/**
 * Branding Context
 *
 * Provides branding configuration throughout the application
 * Handles fetching, caching, and applying brand settings
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import brandingService, { BrandingConfig } from '../services/brandingService';
import { derivePrimaryShades } from '../utils/colorShades';
import { useLanguage } from './LanguageContext';

interface BrandingContextType {
  branding: BrandingConfig;
  loading: boolean;
  refreshBranding: () => Promise<void>;
  applyBranding: (config: BrandingConfig) => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
}

// Single-tenant deployment: /api/system/branding/ IS this tenant's branding,
// so it is applied verbatim. Start with blank name/logo placeholders (never a
// "Nuagent" flash) until it answers. Hostname is deliberately not inspected.
const _onTenantSubdomain = true;

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingConfig>({
    primary_color: '#12717A',
    secondary_color: '#168a96',
    accent_color: '#16a34a',
    // Use an empty/transparent placeholder on tenant subdomains so the
    // header doesn't flash "Nuagent" before the tenant's own logo loads.
    logo_url: _onTenantSubdomain ? '' : '/nuagent-logo.png',
    pdf_logo_url: '',
    company_name: _onTenantSubdomain ? '' : 'Nuagent',
    system_title: 'Business Operations',
    system_title_en: 'Business Operations',
    system_title_ar: 'إدارة الأعمال',
    registration_enabled: true,
    login_design_lockup: true,
  });
  const { applyDefaultLanguage } = useLanguage();
  const [loading, setLoading] = useState(false); // Start as false - don't block rendering

  /**
   * Normalize logo URL to work with the current server configuration
   * Returns relative paths which nginx will proxy to the backend correctly
   * Adds cache-busting query parameter to ensure browser fetches new logo after upload
   */
  const normalizeLogoUrl = (url: string): string => {
    if (!url) return '/nuagent-logo.png';

    // Extract just the path if it's an absolute URL
    let path = url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const urlObj = new URL(url);
        path = urlObj.pathname; // Return just the path
      } catch {
        path = url;
      }
    }

    // For media URLs, add cache-busting parameter to force browser to fetch new image
    // This is important because the logo URL doesn't change when a new logo is uploaded
    if (path.startsWith('/media/')) {
      const cacheBuster = Date.now();
      return `${path}?t=${cacheBuster}`;
    }

    return path;
  };

  /**
   * Fetch branding configuration from API (non-blocking).
   *
   * When the user is on a tenant subdomain, the global /api/system/branding/
   * endpoint still returns the Nuagent default — that endpoint isn't
   * tenant-aware. We override company_name + system_title locally so the
   * header shows the tenant's name instead of "Nuagent".
   */
  const fetchBranding = async () => {
    try {
      const config = await brandingService.getBrandingConfig();
      // Normalize logo URLs to ensure they're relative
      config.logo_url = normalizeLogoUrl(config.logo_url);
      if (config.pdf_logo_url) {
        config.pdf_logo_url = normalizeLogoUrl(config.pdf_logo_url);
      }
      // Same file name on every upload: cache-bust like the logo
      if (config.login_header_url) config.login_header_url = normalizeLogoUrl(config.login_header_url);
      if (config.login_background_url) config.login_background_url = normalizeLogoUrl(config.login_background_url);
      setBranding(config);
      applyBranding(config);
      if (config.default_language) applyDefaultLanguage(config.default_language);

      // Cache in localStorage for faster subsequent loads
      localStorage.setItem('branding_config', JSON.stringify(config));
      localStorage.setItem('branding_timestamp', Date.now().toString());
    } catch (error) {
      console.error('[BrandingContext] Error fetching branding:', error);

      // Try to load from cache if API fails
      const cached = localStorage.getItem('branding_config');
      if (cached) {
        try {
          const cachedConfig = JSON.parse(cached);
          setBranding(cachedConfig);
          applyBranding(cachedConfig);
        } catch (e) {
          console.error('Error parsing cached branding:', e);
          // Keep using default branding if cache also fails
        }
      }
      // If both API and cache fail, defaults are already set in state
    }
  };

  /**
   * Apply branding by updating CSS variables
   */
  const applyBranding = (config: BrandingConfig) => {
    const root = document.documentElement;

    // Update CSS variables
    root.style.setProperty('--mdc-primary', config.primary_color);
    root.style.setProperty('--mdc-secondary', config.secondary_color);
    root.style.setProperty('--mdc-accent', config.accent_color);

    // The Tailwind theme resolves `primary` utilities and the ecme-*
    // component classes from --primary*, not --mdc-primary — write both
    // so tenant branding recolors the whole design system.
    const shades = derivePrimaryShades(config.primary_color);
    root.style.setProperty('--primary', shades.primary);
    root.style.setProperty('--primary-deep', shades.deep);
    root.style.setProperty('--primary-mild', shades.mild);
    root.style.setProperty('--primary-subtle', shades.subtle);
    root.style.setProperty('--primary-rgb', shades.rgb);

    // Update sidebar/header variables (used by .ecme-side-nav in tailwind/index.css)
    root.style.setProperty('--sidebar-bg', config.primary_color);
    root.style.setProperty('--header-bg', config.primary_color);
    root.style.setProperty('--sidebar-text-active', config.primary_color);

    // Update page title (use English version for browser tab)
    document.title = config.system_title_en || config.system_title || 'Application Tracking System';
  };

  /**
   * Refresh branding (useful after updates)
   */
  const refreshBranding = async () => {
    // Clear cache to ensure fresh data
    localStorage.removeItem('branding_config');
    localStorage.removeItem('branding_timestamp');
    await fetchBranding();
  };

  // Load branding on mount
  useEffect(() => {
    // The colours are already on the page: index.html links
    // /api/system/branding/theme.css. Writing the defaults here would paint
    // over them with the bundled teal until the fetch below returns.

    // Check cache version and clear if outdated
    const CACHE_VERSION = '3.0-nuagent'; // Bumped: clear stale MDC-branded caches in users' localStorage
    const cachedVersion = localStorage.getItem('branding_cache_version');

    if (cachedVersion !== CACHE_VERSION) {
      // Clear old cache format
      localStorage.removeItem('branding_config');
      localStorage.removeItem('branding_timestamp');
      localStorage.setItem('branding_cache_version', CACHE_VERSION);
    }

    // Try to load from cache first for immediate display
    const cached = localStorage.getItem('branding_config');
    const timestamp = localStorage.getItem('branding_timestamp');
    const cacheAge = timestamp ? Date.now() - parseInt(timestamp) : Infinity;

    // Use cache if less than 5 minutes old
    if (cached && cacheAge < 5 * 60 * 1000) {
      try {
        const cachedConfig = JSON.parse(cached);
        // Normalize logo URLs in case cached values are absolute
        cachedConfig.logo_url = normalizeLogoUrl(cachedConfig.logo_url);
        if (cachedConfig.pdf_logo_url) {
          cachedConfig.pdf_logo_url = normalizeLogoUrl(cachedConfig.pdf_logo_url);
        }
        setBranding(cachedConfig);
        applyBranding(cachedConfig);
      } catch (e) {
        console.error('Error parsing cached branding:', e);
      }
    }

    // Always fetch fresh data in background (non-blocking)
    fetchBranding();
  }, []);

  const value = {
    branding,
    loading,
    refreshBranding,
    applyBranding
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Custom hook to use branding context
 */
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;
